<script setup lang="ts">
import Tooltip from '@zyro-inc/site-modules/components/Tooltip.vue';

import {
	ref,
	onMounted,
	computed,
	watch,
	nextTick,
	onBeforeUnmount,
} from 'vue';
import {
	EcommerceRegion,
	EcommerceVariantPrice,
	StripeAddressEvent,
	StripeClickEvent,
	StripeConfirmEvent,
	StripeShippingRateEvent,
	EcommerceProduct,
	EcommerceCart,
	EcommerceUpdateCartDataProps,
} from '@zyro-inc/site-modules/types';
import StoreApi from '@zyro-inc/site-modules/api/StoreApi';
import { getSuccessUrl } from '@zyro-inc/site-modules/utils/ecommerce/cartData';
import { useExpressCheckout } from '@zyro-inc/site-modules/use/useExpressCheckout';
import { debounce } from '@zyro-inc/site-modules/utils/debounce';
import {
	getCookie,
	setCookie,
} from '@zyro-inc/site-modules/utils/cookies';
import { COOKIE_AVAILABLE_EXPRESS_CART_IDS } from '@zyro-inc/site-modules/constants/siteModulesConstants';
import Icon from '@zyro-inc/site-modules/components/Icon.vue';

const props = withDefaults(defineProps<{
	isInEditor?: boolean,
	isAddToBagEnabled?: boolean,
	priceData: EcommerceVariantPrice,
	regions?: EcommerceRegion[],
	isPhysical: boolean,
	products: EcommerceProduct[],
	updateCartData?:(payload: EcommerceUpdateCartDataProps) => Promise<EcommerceCart | undefined>;
	expressCartData?: EcommerceCart,
	storeId: string,
	stripePublicKey?: string,
	aboveText?: string,
	translations: Record<string, string>
	hideAboveText?: boolean,
	fixedWidth?: boolean,
	selectedQuantity?: number,
	isShoppingCart?: boolean,
	getExpressCartProducts: () => {
		variantId: string,
		quantity: number,
		customFieldValues?: {
			custom_field_id: string;
			value?: string | undefined
		}[]
	}[]
}>(), {
	hideAboveText: false,
	fixedWidth: true,
	selectedQuantity: 1,
	isShoppingCart: false,
});

const emit = defineEmits<{
	'create-express-cart': [],
	'button-click': [],
	'get-regions': [],
}>();

const isMounted = ref(false);
const successUrl = ref('');
const isLoaded = ref(false);
const paymentType = ref('');
const expressCheckoutElementContainerRef = ref<HTMLElement | null>(null);

const productPrice = computed(() => props.priceData.sale_amount || props.priceData.amount);
const textAboveButtons = computed(() => props.aboveText ?? props.translations.expressCheckout);

const {
	stripe,
	expressCheckoutElement,
	elements,
	shippingRates,
	getShippingRates,
	handleAddressChangeEvent,
	handleShippingRateChange,
	setupStripeElements,
	updateStripeElement,
	totalPriceAmount,
	isStripeMounted,
	updateExpressCartProducts,
	setSelectedShippingRate,
} = useExpressCheckout({
	productPrice,
	isProductPhysical: computed(() => props.isPhysical), // to keep reactivity since values change on load
	updateCartData: props.updateCartData,
	currencyCode: computed(() => props.priceData.currency.code.toLowerCase()),
	selectedQuantity: computed(() => props.selectedQuantity),
});

// Button disabled when in editor
// OR custom field is not filled
const isClickDisabled = computed(() => props.isInEditor || !props.isAddToBagEnabled);
const allowedRegionCodeList = computed(() => props.regions?.flatMap(
	({ countries }) => countries.map((country) => country.countryCode.toUpperCase()),
) || []);
const elementOptions = computed(() => ({
	emailRequired: true,
	phoneNumberRequired: true,
	shippingAddressRequired: props.isPhysical,
	// only pass countries if product is physical, otherwise we allow all countries
	...(props.isPhysical ? {
		allowedShippingCountries: allowedRegionCodeList.value,
	} : {}),
	shippingRates: shippingRates.value,
}));

const initialisePaymentSession = async (event: StripeConfirmEvent): Promise<EcommerceCart | undefined> => {
	let cart;

	try {
		cart = await StoreApi.initiatePaymentSession(props.expressCartData?.id as string);
	} catch (error) {
		console.error(error);

		event.paymentFailed({
			reason: 'fail',
		});
	}

	return cart;
};

const updateCart = async () => {
	await updateExpressCartProducts({
		cartId: props.expressCartData?.id as string,
		itemsIdsToAdd: props.getExpressCartProducts(),
	});
};

const resolveStripeActions = async () => {
	// Handle click
	expressCheckoutElement.value.on('click', async (event: StripeClickEvent) => {
		if (!props.isAddToBagEnabled) {
			return;
		}

		paymentType.value = event.expressPaymentType;
		event.resolve(elementOptions.value);
	});
	// Handle shipping address change
	expressCheckoutElement.value.on('shippingaddresschange', async (event: StripeAddressEvent) => {
		const result = await handleAddressChangeEvent(event, props.expressCartData?.id as string, props.regions);

		if (result?.resolve) {
			event.resolve({
				shippingRates: shippingRates.value,
			});
		}
	});
	// Handle shipping rate change
	expressCheckoutElement.value.on('shippingratechange', async (event: StripeShippingRateEvent) => {
		const result = await handleShippingRateChange(event, props.expressCartData?.id as string);

		// @ts-ignore
		if (!result?.preventResolve) {
			event.resolve();
		}
	});

	// Handle Confirm
	expressCheckoutElement.value.on('confirm', async (event: StripeConfirmEvent) => {
		await handleAddressChangeEvent(event, props.expressCartData?.id as string, props.regions);
		const cart = await initialisePaymentSession(event);

		const clientSecret = cart?.payment_session?.data.payment_intent_client_secret;

		if (clientSecret) {
			stripe.value?.confirmPayment({
				clientSecret,
				elements: elements.value,
				confirmParams: {
					return_url: successUrl.value,
					payment_method: paymentType.value,
				},
			}).then((result: {error?: Error}) => {
				if (result.error) {
					window.history.pushState({}, '', `${window.location.pathname}?open-modal=EcommerceCheckoutFailed`);
				}
			});
		}
	});

	expressCheckoutElement.value.on('cancel', () => {
		if (shippingRates.value.length) {
			setSelectedShippingRate(shippingRates.value[0]);
		}
	});
};

const initiateExpressCheckout = async () => {
	const location = props.isShoppingCart ? 'shopping_cart' : 'product_page';

	await setupStripeElements(props.stripePublicKey as string, expressCheckoutElementContainerRef.value, location);

	isLoaded.value = true;

	if (props.isInEditor) {
		return;
	}

	successUrl.value = getSuccessUrl(props.products, !props.isShoppingCart);

	emit('get-regions');

	await getShippingRates(props.expressCartData);
	await updateCart();
	await resolveStripeActions();
};

const saveCartIdToCookie = () => {
	if (props.expressCartData?.id) {
		const cookieValue = getCookie(COOKIE_AVAILABLE_EXPRESS_CART_IDS) ?? '';
		const availableCartIds = cookieValue.length ? cookieValue.split(',') : [];

		if (!availableCartIds.includes(props.expressCartData?.id)) {
			setCookie(COOKIE_AVAILABLE_EXPRESS_CART_IDS, [
				...availableCartIds,
				props.expressCartData?.id,
			].join(','), 30);
		}
	}
};

onMounted(() => {
	isMounted.value = true;
	window.addEventListener('beforeunload', saveCartIdToCookie);
});

watch([
	() => props.expressCartData?.id,
	() => props.stripePublicKey,
	isMounted,
	totalPriceAmount,
], () => {
	if (props.expressCartData?.id && props.stripePublicKey && isMounted.value && totalPriceAmount.value > 0) {
		initiateExpressCheckout();
	}
});

watch([
	productPrice,
	() => props.selectedQuantity,
], async (newValue, oldValue) => {
	if (newValue !== oldValue && newValue) {
		await nextTick();

		updateStripeElement({
			price: totalPriceAmount.value,
		});
	}
});

watch(() => props.getExpressCartProducts(), debounce(async () => {
	await updateCart();
}, 1000));

onBeforeUnmount(() => {
	saveCartIdToCookie();
	window.removeEventListener('beforeunload', saveCartIdToCookie);
});

</script>

<template>
	<div
		v-qa="'express-checkout-element'"
		class="express-checkout"
	>
		<div
			v-if="!hideAboveText && textAboveButtons?.length && isStripeMounted"
			class="express-checkout__title-element"
		>
			<div class="express-checkout__text-container">
				<p class="express-checkout__text">
					{{ textAboveButtons }}
				</p>
				<Tooltip
					class="express-checkout"
					position="top"
					size="large"
					toggle-event="hover"
					content-position="absolute"
					mode="dark"
					:use-portal="false"
					:forced-position="{
						bottom: '30px',
						left: '50%'
					}"
				>
					<template #trigger>
						<Icon
							name="info"
							:width="16"
							:height="16"
							color="gray"
						/>
					</template>

					<p class="express-checkout__tooltip-content">
						<b>{{ translations.expressCheckoutMissingCheckoutButtons }}</b>
						{{ translations.expressCheckoutMissingCheckoutButtonsSuggestion }}
					</p>
				</Tooltip>
			</div>
		</div>
		<div
			ref="expressCheckoutElementContainerRef"
			:class="{
				'express-checkout__element': expressCheckoutElement,
				'express-checkout__element--disabled': isClickDisabled,
				'express-checkout__element--fixed-width': fixedWidth,
			}"
			@click="$emit('button-click')"
		/>
	</div>
</template>

<style scoped lang="scss">
.express-checkout {
	:deep(.tooltip__content) {
		min-width: 280px;
		transform: translate(-50%);
	}

	&__title-element {
		display: flex;
		position: relative;
		width: 100%;
		justify-content: center;
		align-items: center;
		gap: 16px;
		margin-top: 24px;
		color: var(--color-gray);

		&::before, &::after {
			content: '';
			height: 1px;
			width: 100%;
			background: var(--color-gray);
		}
	}

	&__text-container {
		display: flex;
		flex-shrink: 0;
		gap: 4px;
		align-items: center;
	}

	&__text {
		max-width: 280px;
		flex: 0 0 auto;
		color: var(--color-gray);
		font-size: 14px;
		line-height: 1.42;
	}

	&__tooltip-content {
		color: var(--color-light);
		text-align: center;
	}

	&__tooltip-icon {
		margin-left: 4px;
	}

	&__element {
		margin-top: 16px;

		&--disabled {
			cursor: pointer;

			:deep() {
				iframe {
					pointer-events: none;
				}
			}
		}

		&--fixed-width {
			width: 340px;
		}
	}
}
</style>
